import { Box, IconButton, Tooltip } from "@mui/material";
import { darken } from "@mui/material/styles";
import { encampBrandColors } from "colors";
import ScoutIcon from "components/ScoutIcon";
import { useRef } from "react";

export type ScoutFooterLauncherVariant = "closed" | "minimized";

export type ScoutFooterLauncherProps = {
  variant: ScoutFooterLauncherVariant;
  onClick: () => void;
  launcherLeft: number;
  launcherZIndex: number;
  isStreaming?: boolean;
  showCompletionBadge?: boolean;
};

const COMPLETION_POP_ANIMATION = "scoutCompletionPop 0.7s ease-out";

function getLauncherTooltip(input: {
  variant: ScoutFooterLauncherVariant;
  isStreaming: boolean;
  showCompletionBadge: boolean;
}): string {
  if (input.variant === "closed") {
    return "Ask Scout";
  }
  if (input.isStreaming) {
    return "Scout is thinking…";
  }
  if (input.showCompletionBadge) {
    return "Scout has a new response";
  }
  return "Resume Scout";
}

function getLauncherAriaLabel(input: {
  variant: ScoutFooterLauncherVariant;
  isStreaming: boolean;
  showCompletionBadge: boolean;
}): string {
  if (input.variant === "closed") {
    return "Ask Scout";
  }
  if (input.isStreaming) {
    return "Scout is thinking";
  }
  if (input.showCompletionBadge) {
    return "Scout has a new response";
  }
  return "Resume Scout";
}

/**
 * Fixed-position circular Scout launcher used for both the idle entry point
 * and the minimized popover state. Minimized mode can spin while a turn is
 * in flight; when a response completes unseen the icon pops once and the
 * launcher glows until the user reopens the popover.
 */
export function ScoutFooterLauncher({
  variant,
  onClick,
  launcherLeft,
  launcherZIndex,
  isStreaming = false,
  showCompletionBadge = false,
}: ScoutFooterLauncherProps) {
  const previousShowCompletionRef = useRef(showCompletionBadge);
  const completionPopEpochRef = useRef(0);

  if (showCompletionBadge && !previousShowCompletionRef.current) {
    completionPopEpochRef.current += 1;
  }
  previousShowCompletionRef.current = showCompletionBadge;

  const tooltip = getLauncherTooltip({ variant, isStreaming, showCompletionBadge });
  const ariaLabel = getLauncherAriaLabel({ variant, isStreaming, showCompletionBadge });

  const iconAnimation = isStreaming
    ? "scoutSpin 1.1s linear infinite"
    : showCompletionBadge
      ? COMPLETION_POP_ANIMATION
      : "none";
  const iconKey = showCompletionBadge ? `completion-${completionPopEpochRef.current}` : "idle";

  return (
    <Tooltip
      title={tooltip}
      placement="right"
      enterDelay={100}
      disableInteractive
      slotProps={{ tooltip: { sx: { pointerEvents: "none" } } }}
    >
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          left: launcherLeft,
          zIndex: launcherZIndex,
          transition: "left 200ms ease",
        }}
      >
        <IconButton
          onClick={onClick}
          aria-label={ariaLabel}
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: encampBrandColors.pumpkin,
            color: "white",
            boxShadow: showCompletionBadge
              ? "0 0 0 2px rgba(255, 255, 255, 0.95), 0 0 16px 4px rgba(255, 255, 255, 0.5), 0 0 32px 10px rgba(255, 255, 255, 0.28)"
              : 3,
            transition: "transform 150ms ease, box-shadow 200ms ease",
            "&:hover": {
              bgcolor: darken(encampBrandColors.pumpkin, 0.15),
              transform: "scale(1.08)",
              boxShadow: showCompletionBadge
                ? "0 0 0 2px rgba(255, 255, 255, 1), 0 0 20px 6px rgba(255, 255, 255, 0.55), 0 0 36px 12px rgba(255, 255, 255, 0.32)"
                : 6,
            },
            "&:active": {
              transform: "scale(0.96)",
            },
          }}
        >
          <ScoutIcon
            key={iconKey}
            sx={{
              fontSize: 28,
              color: "white",
              animation: iconAnimation,
              transformOrigin: "center",
              "@keyframes scoutSpin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
              "@keyframes scoutCompletionPop": {
                "0%": { transform: "scale(1)" },
                "35%": { transform: "scale(1.22)" },
                "65%": { transform: "scale(1.22)" },
                "100%": { transform: "scale(1)" },
              },
            }}
          />
        </IconButton>
      </Box>
    </Tooltip>
  );
}
